'use strict';

import { CONST } from 'app_vodafone_ziggo/util/util';

/**
 * @class VZTcBaseCard
 * @extends HTMLElement
 * @description Base class for Teams Calling product cards that handles common functionality
 */
export default class VZTcBaseCard extends HTMLElement {
    constructor() {
        super();
    }

    /**
     * @function connectedCallback
     * @description Lifecycle callback when element is added to the DOM
     */
    connectedCallback() {
        this.initializeCard();

        this.handleContentUpdate = this.handleContentUpdate.bind(this);
        document.addEventListener('teamscalling:contentupdate', this.handleContentUpdate);
    }

    /**
     * @function disconnectedCallback
     * @description Removes event listeners when element is removed from the DOM
     */
    disconnectedCallback() {
        if (this.contentToggle) {
            this.contentToggle.removeEventListener('change', this.boundToggleContent);
        }

        document.removeEventListener('teamscalling:contentupdate', this.handleContentUpdate);
    }

    /**
     * @function initializeCard
     * @description Initializes the card by setting up DOM references and content toggle if present
     */
    initializeCard() {
        // Product Cards
        this.productCard = this.closest('.js-tc-product-card');
        this.cardContainer = this.parentElement.closest('.js-teams-calling-product-card');

        // Product
        this.productId = this.getAttribute('data-product-id');

        // Question
        this.questionId = this.getAttribute('data-question-id');

        // Ensure cards and sub cards are initialized in the correct order to
        // avoid unexpected behaviour in enable/disable validation below
        this.cards = Array.from(document.querySelectorAll('.js-teams-calling-product-card'))
            .map((card) => ({
                element: card,
                questionId: card.querySelector('[data-question-id]')?.getAttribute('data-question-id') || '0'
            }))
            .sort((a, b) => VZTcBaseCard.compareQuestionIds(a.questionId, b.questionId));
        this.cardIndex = this.cards.findIndex((card) => card.questionId === this.questionId);

        if (!this.ensureInitializationOrder()) return; // Defer if parent not yet initialized

        // Sub Questions
        this.subQuestionIds = this.getAttribute('data-sub-question-ids');
        this.subQuestionType = this.getAttribute('data-sub-question-type') || '';
        this.subQuestionId = this.getAttribute('data-sub-question-id');
        this.subQuestion = document.getElementById(this.subQuestionId);

        // Existing Config
        this.config = JSON.parse(document.querySelector('.js-active-configuration').value) || {};

        // Validations
        this.shouldEnableNextCard = this.getAttribute('data-enable-next-card') === 'true';
        this.isCardAnswered = this.getAttribute('data-card-answered') === 'true';

        // Find any answered sub cards and use those options to validate enabling the next card
        const secondaryCards = Array.from(this.cardContainer.querySelectorAll(`.js-question[data-question-id="${this.questionId}"]`));
        const tertiaryCards = Array.from(this.productCard.querySelectorAll(`.js-question-optional[data-question-id="${this.questionId}"]`));

        if (secondaryCards.length > 1) {
            const answeredCard = secondaryCards.find((card) => card.dataset.cardAnswered === 'true') || tertiaryCards.find((card) => card.dataset.cardAnswered === 'true');

            if (answeredCard) {
                const shouldEnableNextCard = answeredCard.getAttribute('data-enable-next-card') === 'true';
                const isCardAnswered = answeredCard.getAttribute('data-card-answered') === 'true';

                this.cardContainer.setAttribute('data-sub-cards-answered', shouldEnableNextCard && isCardAnswered);
            }
        }

        this.noSubProducts = this.getAttribute('data-no-sub-products');
        this.hasTeamsCallingInBasket = this.productCard.getAttribute('data-has-teams-calling-in-basket') === 'true';
        this.editMode = this.productCard.getAttribute('data-edit-mode') === 'true';

        // Previous Card
        this.previousCardContainer = this.findPreviousCard();
        if (this.previousCardContainer) {
            // Is Previous Card Answered?
            this.isPreviousCardAnswered = VZTcBaseCard.isCardAnswered(this.previousCardContainer);
        }

        // Next Card
        this.nextCardContainer = this.findNextCard();
        if (this.nextCardContainer) {
            // Is Next Card Answered?
            this.isNextCardAnswered = VZTcBaseCard.isCardAnswered(this.nextCardContainer);
        }

        // Content Toggles
        if (this.hasContentToggle()) this.setupContentToggle();

        if (this.hasTeamsCallingInBasket && this.editMode) {
            // When in edit mode we want to avoid all the complex enable/disable logic
            // and just enable all cards for the existing basket configuration
            VZTcBaseCard.enableCard(this.cardContainer);

            this.validateCard(true);
        } else if (!this.hasTeamsCallingInBasket) {
            // Enable First Card
            if (this.questionId === '1') {
                VZTcBaseCard.enableCard(this.cardContainer);

                if (this.shouldEnableNextCard && this.isCardAnswered) this.validateCard(true);
                // Enable Next Card (If previous card is enabled and answered)
            } else if (this.isPreviousCardAnswered && !this.isCardDisabled(this.previousCardContainer)) {
                VZTcBaseCard.enableCard(this.cardContainer);

                // Validate Next Card State
                const contentBlock = this.closest('.js-question-content-block');
                const isHidden = contentBlock ? contentBlock.classList.contains(CONST.classes.dNone) : false;

                if (!isHidden) this.validateCard(this.shouldEnableNextCard && this.isCardAnswered);
            } else {
                this.validateCard(false, this.cardContainer);
            }
        }
    }

    static parseQuestionId(questionId) {
        // Parse question ID for hierarchical sorting (3, 3_1, 3_2, etc)
        return questionId.split('_').map((part) => parseInt(part, 10));
    }

    static compareQuestionIds(a, b) {
        // Compare question IDs for hierarchical sorting (3, 3_1, 3_2, etc)
        const aParts = VZTcBaseCard.parseQuestionId(a);
        const bParts = VZTcBaseCard.parseQuestionId(b);

        // Compare parent question numbers
        if (aParts[0] !== bParts[0]) {
            return aParts[0] - bParts[0];
        }

        // Same parent question, compare sub question numbers
        const aSubPart = aParts[1] || 0;
        const bSubPart = bParts[1] || 0;

        return aSubPart - bSubPart;
    }

    /**
     * @function ensureInitializationOrder
     * @description Ensures cards are initialized in the correct sequential order
     * @returns {boolean} Whether initialization should proceed
     */
    ensureInitializationOrder() {
        // If current question is a sub question, check if its parent is initialized
        if (this.questionId.includes('_')) {
            const parentQuestionId = this.questionId.split('_')[0];
            const parentCard = this.cards.find((card) => card.questionId === parentQuestionId);

            if (parentCard) {
                const parentCardComponent = parentCard.element.querySelector('[data-question-id]');

                if (parentCardComponent && !parentCardComponent.hasAttribute('data-initialized')) {
                    // Parent card not initialized yet, defer this initialization
                    setTimeout(() => this.initializeCard(), 10);

                    return false;
                }
            }
        }

        // Check if any previous cards in the sequence are not yet initialized
        for (let i = 0; i < this.cardIndex; i++) {
            const previousCard = this.cards[i];
            const previousCardComponent = previousCard.element.querySelector('[data-question-id]');

            // Only check parent questions as blockers (not sub-questions)
            if (!previousCard.questionId.includes('_') && previousCardComponent && !previousCardComponent.hasAttribute('data-initialized')) {
                // Previous parent card not initialized yet, defer this initialization
                setTimeout(() => this.initializeCard(), 10);

                return false;
            }
        }

        // Mark card as initialized
        this.setAttribute('data-initialized', 'true');

        return true;
    }

    /**
     * @function hasContentToggle
     * @description Checks if the card has content toggle functionality and sets up related properties
     * @returns {boolean} Whether the card has content toggle elements
     */
    hasContentToggle() {
        this.contentToggle = this.querySelector('input.js-question-content-toggle');
        this.contentBlocks = Array.from(document.querySelectorAll(`.js-question-content-block[data-question-id="${this.questionId}"]`));

        return this.contentToggle && this.contentBlocks.length > 0;
    }

    /**
     * @function setupContentToggle
     * @description Sets up event listeners for content toggle functionality
     */
    setupContentToggle() {
        this.boundToggleContent = this.toggleContent.bind(this);
        this.contentToggle.addEventListener('change', this.boundToggleContent);
    }

    /**
     * @function toggleContent
     * @description Handles radio toggle for cards with hidden content based on user input. Emits a configuration update event.
     * @param {Event} event - The change event from the toggle input
     */
    toggleContent(event) {
        // Emit configuration update event
        const questionId = this.getAttribute('data-question-id');
        const answerId = Array.from(this.parentElement.querySelectorAll('.js-question-content-toggle')).findIndex((radio) => radio === event.target);

        const configUpdateEvent = new CustomEvent('teamscalling:configupdate', {
            bubbles: true,
            detail: {
                type: 'radio',
                subType: this.subQuestionType,
                questionId,
                value: answerId.toString(),
                subQuestionIds: this.subQuestionIds,
                defaultSubQuestionAnswer: this.getDefaultSubQuestionAnswer()
            }
        });

        this.dispatchEvent(configUpdateEvent);
    }

    /**
     * @function enableNextCard
     * @description Enables the next card in the sequence if it's a Teams Calling card
     */
    enableNextCard() {
        if (this.nextCardContainer?.classList.contains('js-teams-calling-product-card')) {
            this.nextCardContainer.removeAttribute('data-card-disabled');
            this.nextCardContainer.classList.remove('tc-product-card--disabled');
        }
    }

    /**
     * @function disableNextCard
     * @description Disables the next card in the sequence if it's a Teams Calling card
     */
    disableNextCard() {
        if (this.nextCardContainer?.classList.contains('js-teams-calling-product-card')) {
            this.nextCardContainer.setAttribute('data-card-disabled', true);
            this.nextCardContainer.classList.add('tc-product-card--disabled');
        }
    }

    /**
     * @function enableCard
     * @description Enables the specified card if it's a Teams Calling card
     * @param {HTMLElement} cardContainer - The container element to be enabled
     */
    static enableCard(cardContainer) {
        if (cardContainer?.classList.contains('js-teams-calling-product-card')) {
            cardContainer.removeAttribute('data-card-disabled');
            cardContainer.classList.remove('tc-product-card--disabled');
        }
    }

    /**
     * @function disableCard
     * @description Disables the specified card if it's a Teams Calling card
     * @param {HTMLElement} cardContainer - The container element to be disabled
     */
    static disableCard(cardContainer) {
        if (cardContainer?.classList.contains('js-teams-calling-product-card')) {
            cardContainer.setAttribute('data-card-disabled', true);
            cardContainer.classList.add('tc-product-card--disabled');
        }
    }

    /**
     * @function findPreviousCard
     * @description Finds the previous Teams Calling card in the sequence
     * @returns {Element} The previous card element
     */
    findPreviousCard() {
        const previousCard = Array.from(this.cardContainer.parentElement.children)
            .filter((el) => el.classList.contains('js-teams-calling-product-card'))
            .find((el, i, arr) => arr[i + 1] === this.cardContainer);

        return previousCard;
    }

    /**
     * @function findNextCard
     * @description Finds the next Teams Calling card in the sequence
     * @returns {Element} The next card element
     */
    findNextCard() {
        const nextCard = Array.from(this.cardContainer.parentElement.children)
            .filter((el) => el.classList.contains('js-teams-calling-product-card'))
            .find((el, i, arr) => arr[i - 1] === this.cardContainer);

        return nextCard;
    }

    /**
     * @function validateCard
     * @description Checks if card container status should be enabled or disabled. By default card container will be the next Teams Calling product card.
     * @param {boolean} argument - Argument to validate against
     * @param {HTMLElement} cardContainer - Optional specified card container to be validated
     */
    validateCard(argument, cardContainer = null) {
        // Specific card container
        if (cardContainer) {
            if (argument) {
                VZTcBaseCard.enableCard(cardContainer);
            } else {
                VZTcBaseCard.disableCard(cardContainer);
            }

            // Validate sidebar submit button
            this.submitValidate();

            return;
        }

        // Next card container
        if (argument) {
            this.enableNextCard();
        } else {
            this.disableNextCard();
        }

        // Validate sidebar submit button
        this.submitValidate();
    }

    /**
     * @function isCardAnswered
     * @description Checks if a card container has been answered
     * @param {HTMLElement} cardContainer - The card container to check
     * @returns {boolean} Whether the card has been answered
     */
    static isCardAnswered(cardContainer) {
        const subCardsAnswered = cardContainer.getAttribute('data-sub-cards-answered');

        return subCardsAnswered ? subCardsAnswered === 'true' : cardContainer.querySelectorAll('[data-card-answered="true"]').length > 0;
    }

    /**
     * @function isCardDisabled
     * @description Confirm if a card is disabled
     * @param {HTMLElement} cardContainer - Optional specified card container to be validated. Default is current card container.
     * @returns {boolean} Whether a card container has a disabled state
     */
    isCardDisabled(cardContainer = this.cardContainer) {
        const isProductCard = cardContainer?.classList.contains('js-teams-calling-product-card');
        const isDisabled = cardContainer?.getAttribute('data-card-disabled') === 'true';

        return isProductCard && isDisabled;
    }

    /**
     * @function submitValidate
     * @description Emits a custom event to trigger submit button validation
     */
    submitValidate() {
        this.productCard.dispatchEvent(new CustomEvent('teamscalling:submitvalidate'));
    }

    /**
     * @function updateConfig
     * @description Emits a configuration update event
     * @param {string} type - The type of the update (e.g., 'quantity', 'radio')
     * @param {string} questionId - The ID of the question
     * @param {string} value - The value to update
     * @param {string} options - Optional settings
     */
    updateConfig(type, questionId, value, options = {}) {
        const { subQuestionType } = options;
        const subType = subQuestionType || this.subQuestionType;

        // Value did not change, no update needed
        this.config = JSON.parse(document.querySelector('.js-active-configuration').value) || {};

        const configUpdateEvent = new CustomEvent('teamscalling:configupdate', {
            bubbles: true,
            detail: {
                type: type,
                subType: subType,
                questionId: questionId,
                value: value
            }
        });

        this.dispatchEvent(configUpdateEvent);
    }

    /**
     * @function getDefaultSubQuestionAnswer
     * @description Gets the default answer for a sub question based on data attributes on the card. This is so we can pre-select an answer without doing an extra config update.
     * @returns {Object|boolean} The details object for the answer
     * @protected
     */
    getDefaultSubQuestionAnswer() {
        const defaultSubAnswerType = this.getAttribute('data-default-sub-answer-type');
        const defaultSubAnswer = this.getAttribute('data-default-sub-answer');

        const pid = this.getAttribute('data-default-sub-answer-pid');
        const qty = this.getAttribute('data-default-sub-answer-qty');

        if (!pid) return null;

        const value = { pid: pid };
        if (qty) value.qty = qty;

        return {
            type: defaultSubAnswerType,
            questionId: defaultSubAnswer,
            value: value
        };
    }

    /**
     * @function getUrl
     * @description Gets the URL from data-url attribute and adds current parameters
     * @returns {URL} The URL with configuration parameter
     * @protected
     */
    getUrl() {
        if (!this.productCard) {
            return '';
        }

        const baseUrl = this.productCard.getAttribute('data-url');
        if (!baseUrl) {
            return '';
        }

        // Create URL from baseUrl to preserve existing path
        const url = new URL(baseUrl, window.location.origin);

        const addToCartValues = document.querySelector('.js-add-to-cart-values');

        if (addToCartValues && addToCartValues.dataset.pid) {
            url.searchParams.set('pid', addToCartValues.dataset.pid);
        }

        // Get configuration from hidden input
        const configInput = document.querySelector('.js-active-configuration');

        if (configInput?.value) {
            url.searchParams.append('answers', configInput.value);
        }

        return url;
    }

    /**
     * @function addUrlParams
     * @description Gets the URL from data-url attribute and adds parameters for variation update
     * @param {URL} url - The URL object to which addon parameters will be appended
     * @returns {string} The URL with parameters
     * @protected
     */
    static addUrlParams(url) {
        const addToCartValues = document.querySelector('.js-add-to-cart-values');
        const uuid = document.querySelector('input[name="uuid"]')?.value || null;
        if (uuid) {
            url.searchParams.append('pliId', uuid);
        }

        if (addToCartValues && Object.keys(addToCartValues.dataset).length) {
            const addonsdata = addToCartValues.dataset.addons;
            const addonsqtydata = addToCartValues.dataset.addonsQty;
            const businessRulesAddonsData = addToCartValues.dataset.businessRulesAddons;
            const linesQty = addToCartValues.dataset.linesqty;
            const usersQty = addToCartValues.dataset.usersqty;
            const porting = addToCartValues.dataset.porting;

            url.searchParams.append('addons', addonsdata);
            url.searchParams.append('addonsQty', addonsqtydata);
            url.searchParams.append('businessrulesproducts', businessRulesAddonsData);
            url.searchParams.append('linesQty', linesQty);
            url.searchParams.append('usersQty', usersQty);
            url.searchParams.append('porting', porting);
        }

        return url.toString();
    }

    /**
     * @function updateContent
     * @description Updates specific content areas based on provided selectors
     * @protected
     */
    async updateContent() {
        this.productCard = this.closest('.js-tc-product-card');

        const spinner = document.querySelector('vz-spinner') || new window.VZSpinner();
        const url = this.getUrl();
        const stringUrl = url.toString();

        if (!stringUrl || stringUrl === this.productCard.configUrl) return;

        // Manually cache config url
        this.productCard.configUrl = stringUrl;

        try {
            spinner.start();
            const response = await fetch(VZTcBaseCard.addUrlParams(url));

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const htmlContent = await response.text();

            this.productCard.dispatchEvent(
                new CustomEvent('variationChange', {
                    detail: {
                        response: htmlContent
                    }
                })
            );
        } catch (error) {
            console.error('Error updating content:', error);
        } finally {
            // Validate sidebar submit button
            this.submitValidate();

            spinner.stop();
        }
    }

    handleContentUpdate() {
        this.updateContent();
    }
}
